/* eslint-disable react/prop-types */
/**
 * Presentational YouTube analytics screen. Data and navigation come from props
 * (real or mock container); no API or mock/real branching here.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Layout,
  Row,
  Space,
  Spin,
  Statistic,
  Typography,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import functions from "../../../components/utils/functions";
import LicenseAgreementModal from "./LicenseAgreementModal";
import LicenseAgreementGateScreen from "./LicenseAgreementGateScreen";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const CREATOR_REVENUE_CURRENCY = "INR";
const CREATOR_REVENUE_PREFIX = "₹";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const chartAxisColor = "#a3a3a3";
const chartGridColor = "#262626";

const lineOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
      labels: { color: chartAxisColor },
    },
    title: { display: false },
  },
  scales: {
    x: {
      ticks: { color: chartAxisColor, autoSkip: true, maxRotation: 45 },
      grid: { color: chartGridColor },
    },
    y: {
      ticks: { color: chartAxisColor },
      grid: { color: chartGridColor },
    },
  },
};

export default function YoutubeAnalyticsView({
  loadingProject,
  bindingLoaded = true,
  isLive,
  breadcrumbSlot,
  backHref,
  noticeSlot,
  binding,
  chartAreaLoading,
  selectedMonth,
  onSelectedMonthChange,
  estimates,
  emptyEstimatesDescription,
  contractConfig = null,
  contractRequired = false,
  contractSigned = false,
  contractGateActive = false,
  canViewPage = true,
  bypassContractGate = false,
  contractSigning = false,
  onContractSign,
}) {
  const licenseModal = contractConfig ? (
    <LicenseAgreementModal
      contractConfig={contractConfig}
      signed={contractSigned}
      mandatory={contractGateActive}
      open={contractGateActive}
      onSign={onContractSign}
      signing={contractSigning}
      showTriggerButton={contractSigned && canViewPage}
    />
  ) : null;
  const monthMetrics = useMemo(() => {
    const rows = estimates || [];
    let revenue = 0;
    let views = 0;
    let minutes = 0;
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const r = row.revenue_usd_estimate;
      if (r != null && !Number.isNaN(Number(r))) revenue += Number(r);
      const v = row.views_estimate;
      if (v != null && !Number.isNaN(Number(v))) views += Number(v);
      const m = row.estimated_minutes_watched;
      if (m != null && !Number.isNaN(Number(m))) minutes += Number(m);
    }
    return { revenue, views, minutes };
  }, [estimates]);

  const chartData = useMemo(() => {
    const labels = (estimates || []).map((r) => r.estimate_date);
    const rev = (estimates || []).map((r) =>
      r.revenue_usd_estimate == null ? null : Number(r.revenue_usd_estimate)
    );
    const views = (estimates || []).map((r) =>
      r.views_estimate == null ? null : Number(r.views_estimate)
    );
    return {
      labels,
      datasets: [
        {
          label: `Estimated revenue (${CREATOR_REVENUE_CURRENCY})`,
          yAxisID: "y",
          data: rev,
          borderColor: "rgb(250, 173, 20)",
          backgroundColor: "rgba(250, 173, 20, 0.15)",
          tension: 0.2,
          fill: true,
          spanGaps: false,
        },
        {
          label: "Views (estimated)",
          data: views,
          borderColor: "rgb(64, 169, 255)",
          backgroundColor: "rgba(64, 169, 255, 0.08)",
          tension: 0.2,
          fill: false,
          yAxisID: "y1",
        },
      ],
    };
  }, [estimates]);

  const chartOptions = useMemo(
    () => ({
      ...lineOptions,
      scales: {
        ...lineOptions.scales,
        y: {
          ...lineOptions.scales.y,
          title: {
            display: true,
            text: `${CREATOR_REVENUE_CURRENCY} (estimated)`,
            color: chartAxisColor,
          },
        },
        y1: {
          type: "linear",
          position: "right",
          grid: { drawOnChartArea: false },
          ticks: { color: chartAxisColor },
          title: { display: true, text: "Views", color: chartAxisColor },
        },
      },
    }),
    []
  );

  if (
    loadingProject ||
    (contractRequired && !bindingLoaded && !bypassContractGate)
  ) {
    return (
      <Layout style={{ minHeight: "60vh" }}>
        <Content
          className="vc-panel"
          style={{ padding: 48, textAlign: "center" }}
        >
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  if (contractGateActive && bindingLoaded) {
    return (
      <LicenseAgreementGateScreen
        breadcrumbSlot={breadcrumbSlot}
        backHref={backHref}
        title="YouTube Revenue (estimates)"
        subtitle="Sign the license agreement below to access this page."
        noticeSlot={noticeSlot}
        contractConfig={contractConfig}
        contractSigned={contractSigned}
        contractGateActive={contractGateActive}
        contractSigning={contractSigning}
        onContractSign={onContractSign}
      />
    );
  }

  if (!isLive) {
    return (
      <Layout style={{ minHeight: "60vh" }}>
        <Content className="vc-panel" style={{ padding: 24 }}>
          <Breadcrumb>{breadcrumbSlot}</Breadcrumb>
          <Divider />
          <Alert
            type="info"
            showIcon
            message="Available when you’re live on distribution"
            description="Once your project is live with us, you’ll see day-by-day YouTube estimates here in rupees (or the currency your report uses), alongside your settled streaming revenue on the main analytics page."
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Content
        className="site-layout-background vc-panel"
        style={{
          margin: 0,
          padding: "24px",
          maxWidth: 1120,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {noticeSlot}
        <Breadcrumb>{breadcrumbSlot}</Breadcrumb>
        <Divider />
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Link to={backHref}>
            <ArrowLeftOutlined style={{ fontSize: 20 }} />
          </Link>
          <Title level={3} style={{ margin: 0 }}>
            YouTube Revenue (estimates)
          </Title>
          {licenseModal}
        </div>
        <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
          These numbers come from YouTube’s analytics as estimates. They are not
          a payslip, bank statement, or tax advice.
        </Text>
        <Divider />
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="How this page works"
          description={
            <>
              <Paragraph style={{ marginBottom: 8 }}>
                Revenue shown here is YouTube’s <strong>estimated total</strong>{" "}
                for that video. Your final amount may change at the actual
                payout date.
              </Paragraph>
            </>
          }
        />
        {binding?.platform_link ? (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Text>
              YouTube link:{" "}
              <a
                href={binding.platform_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text code>{binding.platform_link}</Text>
              </a>
            </Text>
          </Card>
        ) : null}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col span={24}>
            <Space wrap size={[8, 8]}>
              <Button
                size="small"
                type={
                  selectedMonth && selectedMonth.isSame(moment(), "month")
                    ? "primary"
                    : "default"
                }
                onClick={() => onSelectedMonthChange(moment().startOf("month"))}
              >
                This month
              </Button>
              <Button
                size="small"
                type={
                  selectedMonth &&
                  selectedMonth.isSame(
                    moment().clone().subtract(1, "month"),
                    "month"
                  )
                    ? "primary"
                    : "default"
                }
                onClick={() =>
                  onSelectedMonthChange(
                    moment().clone().subtract(1, "month").startOf("month")
                  )
                }
              >
                Last month
              </Button>
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={(d) => {
                  if (d) onSelectedMonthChange(d.clone().startOf("month"));
                }}
                format="MMMM YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  Boolean(current && current > moment().endOf("month"))
                }
              />
            </Space>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={8} style={{ marginBottom: 16 }}>
            <Card style={{ width: "100%", height: "100%" }}>
              <Statistic
                title="Est. revenue"
                value={`${CREATOR_REVENUE_PREFIX} ${functions.formatIndianNumber(
                  parseFloat(monthMetrics.revenue),
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} style={{ marginBottom: 16 }}>
            <Card style={{ width: "100%", height: "100%" }}>
              <Statistic
                title="Total views (est.)"
                value={functions.formatIndianNumber(monthMetrics.views, {
                  maximumFractionDigits: 0,
                })}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} style={{ marginBottom: 16 }}>
            <Card style={{ width: "100%", height: "100%" }}>
              <Statistic
                title="Watch time (min, est.)"
                value={functions.formatIndianNumber(monthMetrics.minutes, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })}
              />
            </Card>
          </Col>
        </Row>
        {chartAreaLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : null}
        {!chartAreaLoading && estimates.length === 0 ? (
          <Empty description={emptyEstimatesDescription} />
        ) : null}
        {!chartAreaLoading && estimates.length > 0 ? (
          <>
            <Line data={chartData} options={chartOptions} height={150} />
          </>
        ) : null}
      </Content>
    </Layout>
  );
}
